import { useMemo } from 'react';

import { parseISO } from 'date-fns';
import { AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Modal } from '@/components/modal';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { ButtonLoadable } from '@/components/ui/button-loadable';
import { useScrollOverflow } from '@/hooks/scroll/use-scroll-overflow';
import { ModalProps } from '@/hooks/use-modal';
import { useApiError } from '@/modules/common/hooks/use-api-error';
import { formatWallClockDate, formatWallClockShortDate } from '@/utils/date-formatters';

import {
  EMPTY_SELECTION_COUNTS,
  getApprovalActionTotals,
  getApprovalSelectionCounts,
} from '../../../helpers/approval-selection-counts.helper';
import { countEmptyDescriptions } from '../../../helpers/empty-description.helpers';
import { formatHours } from '../../../helpers/format-time-track.helper';
import { useApproveWorklogs } from '../../../hooks/use-approve-worklogs';
import { useUnapproveWorklogs } from '../../../hooks/use-unapprove-worklogs';
import { GetApprovalFilterRequest } from '../../../types/dto/worklogs-approval.dto';
import {
  ApprovalConfirmMode,
  ApprovalHoursSplit,
  ApprovalSelection,
  ApprovalSelectionLevel,
  ApprovalTimeEntry,
} from '../../../types/time-tracking-approvals';
import { TranslateFn } from './approval-confirm-card';
import { BulkProjectCard, BulkProjectGroup } from './bulk-project-card';
import { SelectedMemberCard } from './selected-member-card';
import { SelectedProjectCard } from './selected-project-card';
import { SingleEntryCard } from './single-entry-card';

interface ApprovalConfirmModalProps extends ModalProps {
  mode: ApprovalConfirmMode;
  /** Null while the modal is closed. */
  selection: ApprovalSelection | null;
  filter: GetApprovalFilterRequest;
}

/**
 * Formats the filter's date range, e.g. "Jun 1 – Jun 30, 2026". The filter dates are
 * UTC-normalized calendar values (`...Z`) — format them as wall-clock so they render
 * literally, not shifted into the viewer's timezone.
 */
const formatFilterDateRange = (filter: GetApprovalFilterRequest): string => {
  if (!filter.startDate || !filter.endDate) return '';
  return `${formatWallClockShortDate(filter.startDate)} – ${formatWallClockDate(filter.endDate)}`;
};

/** Groups the selected worklog entries by project for the bulk worklog-level view. */
const groupEntriesByProject = (entries: ApprovalTimeEntry[]): BulkProjectGroup[] => {
  const map = new Map<number, BulkProjectGroup>();
  for (const entry of entries) {
    const existing = map.get(entry.projectId);
    if (existing) {
      existing.dates.push(parseISO(entry.date));
      existing.totalHours += entry.trackedHours;
      existing.totalApprovedForInvoiceHours += entry.approvedForInvoiceHours;
      existing.totalApprovedForBillHours += entry.approvedForBillHours;
      existing.count += 1;
    } else {
      map.set(entry.projectId, {
        projectId: entry.projectId,
        projectName: entry.projectName,
        dates: [parseISO(entry.date)],
        totalHours: entry.trackedHours,
        totalApprovedForInvoiceHours: entry.approvedForInvoiceHours,
        totalApprovedForBillHours: entry.approvedForBillHours,
        count: 1,
      });
    }
  }
  return Array.from(map.values());
};

export function ApprovalConfirmModal({
  mode,
  open,
  onOpen,
  onClose,
  selection,
  filter,
}: ApprovalConfirmModalProps) {
  const { t } = useTranslation('authenticated.timeTracking');
  const { displayToastErrorFromApiWithFallback } = useApiError();
  const approveWorklogs = useApproveWorklogs();
  const unapproveWorklogs = useUnapproveWorklogs();
  const { mutateAsync, isPending } =
    mode === ApprovalConfirmMode.Approve ? approveWorklogs : unapproveWorklogs;

  // Show the top/bottom dividers only when the body actually scrolls.
  const { ref: scrollRef, isScrollable } = useScrollOverflow([selection]);

  const ns =
    mode === ApprovalConfirmMode.Approve ? 'approvals.approveModal' : 'approvals.unapproveModal';

  const isWorklogLevel = selection?.level === ApprovalSelectionLevel.Worklogs;
  const entries: ApprovalTimeEntry[] = useMemo(
    () => (selection?.level === ApprovalSelectionLevel.Worklogs ? selection.entries : []),
    [selection]
  );
  // Only what the action will actually change (pending for approve, approved for unapprove),
  // net of exclusions and carrying the pending edits — NOT the selection's totals.
  const { count: actionCount, hours: actionHours } = getApprovalActionTotals(
    selection ? getApprovalSelectionCounts(selection) : EMPTY_SELECTION_COUNTS,
    mode
  );
  // Aggregate (project/member) selections always use the bulk copy — they cover many entries.
  const isBulk = isWorklogLevel ? entries.length > 1 : true;

  // Empty-description warning (approve mode only). Worklog level counts the exact entries;
  // aggregate levels rely on the backend `hasEmptyDescription` flag of the selected rows.
  const emptyDescriptionCount =
    mode === ApprovalConfirmMode.Approve && isWorklogLevel ? countEmptyDescriptions(entries) : 0;
  const hasAggregateEmptyDescriptions =
    mode === ApprovalConfirmMode.Approve &&
    ((selection?.level === ApprovalSelectionLevel.Projects &&
      selection.projects.some(({ project }) => project.hasEmptyDescription)) ||
      (selection?.level === ApprovalSelectionLevel.Members &&
        selection.members.some(({ member }) => member.hasEmptyDescription)));
  const hasEmptyDescriptions = emptyDescriptionCount > 0 || hasAggregateEmptyDescriptions;

  const dateRange = formatFilterDateRange(filter);

  const projectGroups = useMemo(
    () => (isWorklogLevel && entries.length > 1 ? groupEntriesByProject(entries) : []),
    [entries, isWorklogLevel]
  );

  // What the overall-totals card sums, per level: how many cards sit above it (the totals are
  // pointless below two) and the tracked hours those cards report.
  const { cardCount, trackedHours } = useMemo(() => {
    switch (selection?.level) {
      case ApprovalSelectionLevel.Projects:
        return {
          cardCount: selection.projects.length,
          trackedHours: selection.projects.reduce(
            (sum, { project }) => sum + project.trackedHours,
            0
          ),
        };
      case ApprovalSelectionLevel.Members:
        return {
          cardCount: selection.members.length,
          trackedHours: selection.members.reduce((sum, { member }) => sum + member.trackedHours, 0),
        };
      case ApprovalSelectionLevel.Worklogs:
        return {
          cardCount: projectGroups.length,
          trackedHours: entries.reduce((sum, entry) => sum + entry.trackedHours, 0),
        };
      default:
        return { cardCount: 0, trackedHours: 0 };
    }
  }, [selection, projectGroups, entries]);

  // Sits under the cards of EVERY level, but only when there is more than one card to sum — with
  // a single card it would repeat that card's figures verbatim.
  const overallTotalsCard = cardCount > 1 && (
    <OverallTotalsCard
      ns={ns}
      t={t}
      trackedHours={trackedHours}
      hours={actionHours}
      entryCount={actionCount}
    />
  );

  const handleConfirm = async () => {
    if (!selection) return;
    try {
      await mutateAsync({ selection, filter });
      onClose();
    } catch (error) {
      displayToastErrorFromApiWithFallback(error, `${ns}.error`);
    }
  };

  return (
    <Modal open={open} onOpen={onOpen} onClose={onClose} className="w-[500px] shadow-xl">
      {/* Header */}
      <div>
        <Modal.Title className="mb-0 text-[22px] font-bold leading-tight tracking-tight">
          {isBulk
            ? t(`${ns}.title.bulk` as Parameters<typeof t>[0])
            : t(`${ns}.title.single` as Parameters<typeof t>[0])}
        </Modal.Title>
        <p className="mt-1.5 text-sm text-muted-foreground">
          {isBulk
            ? t(`${ns}.subtitle.bulk` as Parameters<typeof t>[0])
            : t(`${ns}.subtitle.single` as Parameters<typeof t>[0])}
        </p>
      </div>

      {/* Top divider — only when the body scrolls (mirrors the footer divider). */}
      {isScrollable && <div className="-mx-6 mt-4 h-px bg-border" />}

      {/* Single scroll container for the whole body — long notes/descriptions or
          many bulk cards scroll together here rather than each in its own box.
          Negative horizontal margin cancels the modal's padding so the scrollbar
          sits flush against the modal's right edge; inner padding re-aligns text. */}
      <div
        ref={scrollRef}
        className="scrollbar-thin scrollbar-track-transparent scrollbar-thumb-border -mx-6 flex max-h-[60vh] flex-col space-y-4 overflow-y-auto px-6 py-4"
      >
        {/* Warning banner */}
        <Alert variant="warning">
          <AlertTriangle className="h-5 w-5" strokeWidth={1.8} />
          <AlertDescription className="font-medium leading-[1.55]">
            {isBulk
              ? t(`${ns}.warning.bulk` as Parameters<typeof t>[0])
              : t(`${ns}.warning.single` as Parameters<typeof t>[0])}
          </AlertDescription>
        </Alert>

        {/* Empty description warning */}
        {hasEmptyDescriptions && (
          <Alert variant="warning">
            <AlertTriangle className="h-5 w-5" strokeWidth={1.8} />
            <AlertDescription className="font-medium leading-[1.55]">
              {hasAggregateEmptyDescriptions
                ? t('approvals.emptyDescription.approveWarning.aggregate')
                : isBulk
                  ? t('approvals.emptyDescription.approveWarning.bulk', {
                      count: emptyDescriptionCount,
                      total: entries.length,
                    })
                  : t('approvals.emptyDescription.approveWarning.single')}
            </AlertDescription>
          </Alert>
        )}

        {/* Worklog level, single entry — detail card */}
        {isWorklogLevel && !isBulk && entries[0] && (
          <SingleEntryCard entry={entries[0]} ns={ns} t={t} />
        )}

        {/* Worklog level, bulk — count line + per-project entry groups */}
        {isWorklogLevel && isBulk && (
          <>
            <p className="mb-3.5 text-sm text-foreground">
              {t(`${ns}.countLine` as Parameters<typeof t>[0], { count: entries.length })}
            </p>
            <div className="mb-5">
              <div className="flex flex-col gap-2.5 pb-2">
                {projectGroups.map(group => (
                  <BulkProjectCard key={group.projectId} group={group} ns={ns} t={t} />
                ))}
                {overallTotalsCard}
              </div>
            </div>
          </>
        )}

        {/* Project level — one card per selected project */}
        {selection?.level === ApprovalSelectionLevel.Projects && (
          <>
            <p className="mb-3.5 text-sm text-foreground">
              {t(`${ns}.countLine` as Parameters<typeof t>[0], { count: actionCount })}
            </p>
            <div className="flex flex-col gap-2.5 pb-2">
              {selection.projects.map(selectedProject => (
                <SelectedProjectCard
                  key={selectedProject.project.id}
                  selectedProject={selectedProject}
                  mode={mode}
                  dateRange={dateRange}
                  ns={ns}
                  t={t}
                />
              ))}
              {overallTotalsCard}
            </div>
          </>
        )}

        {/* Member level — one card per selected member */}
        {selection?.level === ApprovalSelectionLevel.Members && (
          <>
            <p className="mb-3.5 text-sm text-foreground">
              {t(`${ns}.countLine` as Parameters<typeof t>[0], { count: actionCount })}
            </p>
            <div className="flex flex-col gap-2.5 pb-2">
              {selection.members.map(selectedMember => (
                <SelectedMemberCard
                  key={selectedMember.member.id}
                  selectedMember={selectedMember}
                  mode={mode}
                  dateRange={dateRange}
                  ns={ns}
                  t={t}
                />
              ))}
              {overallTotalsCard}
            </div>
          </>
        )}
      </div>

      {/* Bottom divider — only when the body scrolls. */}
      {isScrollable && <div className="-mx-6 h-px bg-border" />}

      <Modal.Footer className="mt-5 gap-2.5">
        <Button variant="outline" onClick={onClose} disabled={isPending}>
          {t('common:cancel')}
        </Button>
        <ButtonLoadable isLoading={isPending} disabled={isPending} onClick={handleConfirm}>
          {t(`${ns}.cta` as Parameters<typeof t>[0])}
        </ButtonLoadable>
      </Modal.Footer>
    </Modal>
  );
}

interface OverallTotalsCardProps {
  ns: string;
  t: TranslateFn;
  /** Tracked hours of the cards above, summed. */
  trackedHours: number;
  /** Hours the action writes, per side, summed across the cards above. */
  hours: ApprovalHoursSplit;
  entryCount: number;
}

/**
 * Footer summary of a multi-card selection (worklogs grouped by project, or several selected
 * projects / members): the tracked hours plus both approved totals of the cards above it.
 */
function OverallTotalsCard({ ns, t, trackedHours, hours, entryCount }: OverallTotalsCardProps) {
  return (
    <div className="rounded-[10px] border border-input bg-muted/40 px-[18px] py-3.5">
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-1 text-[13.5px]">
          <span className="flex-shrink-0 text-muted-foreground">
            {t(`${ns}.overallTotal` as Parameters<typeof t>[0])}
          </span>
          <span className="font-semibold text-foreground">
            {t(`${ns}.overallTotalValue` as Parameters<typeof t>[0], {
              hours: formatHours(trackedHours),
              count: entryCount,
            })}
          </span>
        </div>
        <div className="flex items-center gap-1 text-[13.5px]">
          <span className="flex-shrink-0 text-muted-foreground">
            {t(`${ns}.overallApprovedForInvoiceTotal` as Parameters<typeof t>[0])}
          </span>
          <span className="font-semibold text-foreground">{formatHours(hours.invoice)}</span>
        </div>
        <div className="flex items-center gap-1 text-[13.5px]">
          <span className="flex-shrink-0 text-muted-foreground">
            {t(`${ns}.overallApprovedForBillTotal` as Parameters<typeof t>[0])}
          </span>
          <span className="font-semibold text-foreground">{formatHours(hours.bill)}</span>
        </div>
      </div>
    </div>
  );
}
